import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PhoneCall, PhoneOff, Users, Play, Pause, CheckCircle2, XCircle, Clock, Calendar, DollarSign, MessageSquare, Globe2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  dialerPresence, dialerRoster, dialerQueue, dialerClaim, dialerRelease,
  dialerClickToCall, dialerDisposition, dialerHistory, dialerStats,
} from "@/lib/api";

const DISPOSITIONS = [
  { key: "answered", label: "Answered", tone: "emerald" },
  { key: "voicemail", label: "Voicemail", tone: "amber" },
  { key: "no_answer", label: "No answer", tone: "slate" },
  { key: "booked", label: "Booked", tone: "indigo" },
  { key: "converted", label: "Converted 🎉", tone: "emerald" },
  { key: "callback_scheduled", label: "Callback scheduled", tone: "indigo" },
  { key: "not_interested", label: "Not interested", tone: "rose" },
  { key: "unqualified", label: "Unqualified", tone: "slate" },
  { key: "wrong_number", label: "Wrong number", tone: "rose" },
  { key: "do_not_call", label: "Do NOT call", tone: "rose" },
];
const PRESENCE = [
  { key: "available", label: "Available", tone: "emerald" },
  { key: "busy",      label: "Busy",      tone: "amber" },
  { key: "on_break",  label: "On break",  tone: "slate" },
  { key: "offline",   label: "Offline",   tone: "slate" },
];
const LANGS = [
  { key: "en", label: "English" }, { key: "es", label: "Spanish" },
  { key: "fr", label: "French" }, { key: "hi", label: "Hindi" },
  { key: "ar", label: "Arabic" },
];

export default function DialerWorkspace() {
  const [presenceStatus, setPresenceStatus] = useState("available");
  const [queue, setQueue] = useState([]);
  const [roster, setRoster] = useState({ items: [], counts: {} });
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [claim, setClaim] = useState(null);
  const [dial, setDial] = useState({ number: "", language: "en", note: "" });
  const [disp, setDisp] = useState({ disposition: "answered", notes: "", duration_seconds: "", revenue_estimate: "" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    try {
      const [q, r, s] = await Promise.all([dialerQueue(), dialerRoster(), dialerStats()]);
      setQueue(q.items || []); setRoster(r); setStats(s);
    } catch (e) {
      setError(e?.response?.data?.detail || "Sign in required to access the dialer.");
    }
  };
  useEffect(() => { document.title = "Dialer Workspace"; refresh(); }, []);
  useEffect(() => {
    if (!selected) { setHistory(null); setDial({ number: "", language: "en", note: "" }); return; }
    dialerHistory(selected.id).then(setHistory);
    setDial((d) => ({ ...d, number: selected.contact?.phone || "", language: selected.contact?.language || "en" }));
  }, [selected]);

  const heartbeat = async (status) => {
    setPresenceStatus(status);
    try { await dialerPresence(status); refresh(); } catch (e) {}
  };
  useEffect(() => {
    const iv = setInterval(() => { dialerPresence(presenceStatus).catch(() => {}); }, 60_000);
    return () => clearInterval(iv);
  }, [presenceStatus]);

  const claimLead = async (l) => {
    setBusy(true); setNotice(""); setError("");
    try {
      const c = await dialerClaim(l.id);
      setClaim(c); setSelected(l);
      setNotice(`Claimed. TTL 15 min — you own this until you disposition or release.`);
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || "Claim failed."); }
    finally { setBusy(false); }
  };
  const releaseLead = async () => {
    if (!selected) return;
    setBusy(true);
    try { await dialerRelease(selected.id, "manual"); setSelected(null); setClaim(null); setHistory(null); refresh(); }
    catch (e) { setError(e?.response?.data?.detail || "Release failed."); }
    finally { setBusy(false); }
  };
  const clickToCall = async () => {
    if (!selected || !dial.number) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await dialerClickToCall({ lead_id: selected.id, to_number: dial.number, language: dial.language, note: dial.note });
      setNotice("Call initiated (MOCK — real telephony wires in Phase 2).");
      dialerHistory(selected.id).then(setHistory);
    } catch (e) { setError(e?.response?.data?.detail || "Click-to-call failed."); }
    finally { setBusy(false); }
  };
  const submitDisposition = async () => {
    if (!selected) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await dialerDisposition(selected.id, {
        disposition: disp.disposition, notes: disp.notes,
        duration_seconds: disp.duration_seconds ? Number(disp.duration_seconds) : null,
        revenue_estimate: disp.revenue_estimate ? Number(disp.revenue_estimate) : null,
      });
      setNotice(`Disposition '${disp.disposition}' recorded.`);
      setSelected(null); setClaim(null); setHistory(null);
      setDisp({ disposition: "answered", notes: "", duration_seconds: "", revenue_estimate: "" });
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || "Disposition failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="dialer-workspace">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/ai-lead-response" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> AI Lead Response</Link>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Dialer Workspace</h1>
              <p className="mt-2 text-slate-600">Prioritized queue · live team presence · click-to-call · dispositions.</p>
            </div>
            <div className="flex items-center gap-2">
              {PRESENCE.map((p) => (
                <button key={p.key} onClick={() => heartbeat(p.key)}
                  data-testid={`dialer-presence-${p.key}`}
                  className={`text-xs uppercase font-bold px-3 py-1.5 rounded-full border transition ${
                    presenceStatus === p.key
                      ? `bg-${p.tone}-600 text-white border-${p.tone}-700`
                      : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"
                  }`}
                  style={presenceStatus === p.key ? {
                    background: p.tone === "emerald" ? "#059669" : p.tone === "amber" ? "#d97706" : "#475569",
                    color: "white", borderColor: "transparent",
                  } : {}}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {error && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{error}</div>}
          {notice && <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">{notice}</div>}

          {/* Stats + team */}
          {stats && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Tile label="Calls (7d)" value={stats.calls} tone="indigo" testId="dialer-stat-calls" />
              <Tile label="Connected" value={stats.connected} tone="emerald" testId="dialer-stat-connected" />
              <Tile label="Booked" value={stats.booked} tone="amber" testId="dialer-stat-booked" />
              <Tile label="Converted" value={stats.converted} tone="emerald" testId="dialer-stat-converted" />
              <Tile label="Revenue est." value={`$${stats.total_revenue_estimate}`} tone="slate" testId="dialer-stat-rev" />
              <Tile label="Available reps" value={roster.counts.available || 0} tone="emerald" testId="dialer-stat-avail" />
            </div>
          )}

          {/* Roster strip */}
          <section className="mt-6 rounded-2xl bg-white border border-slate-200 p-4" data-testid="dialer-roster">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Team roster</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(roster.items || []).map((u) => (
                <div key={u.user_id} data-testid={`dialer-rep-${u.user_id}`}
                  className={`rounded-full px-3 py-1 text-xs border ${
                    u.status === "available" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                    u.status === "busy" ? "bg-amber-50 border-amber-200 text-amber-800" :
                    u.status === "on_break" ? "bg-slate-100 border-slate-200 text-slate-700" :
                    "bg-slate-50 border-slate-200 text-slate-500"
                  }`}>
                  <strong>{u.user_email?.split("@")[0]}</strong> · {u.status}
                </div>
              ))}
              {(roster.items || []).length === 0 && <div className="text-xs text-slate-500">No live reps yet — set your presence above.</div>}
            </div>
          </section>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Queue */}
            <section className="md:col-span-1 rounded-2xl bg-white border border-slate-200 p-3" data-testid="dialer-queue">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold p-2">Queue ({queue.length})</div>
              <div className="space-y-2 max-h-[640px] overflow-y-auto">
                {queue.length === 0 && <div className="p-4 text-center text-sm text-slate-500">Nothing to dial. 🎉</div>}
                {queue.map((l) => (
                  <button key={l.id} onClick={() => claimLead(l)} disabled={l.claim}
                    data-testid={`dialer-queue-item-${l.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition ${
                      l.claim ? "border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed"
                              : selected?.id === l.id
                                ? "border-indigo-500 bg-indigo-50"
                                : "border-slate-200 hover:border-indigo-300"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold text-slate-900 truncate">{l.contact?.name || l.contact?.phone || l.contact?.email || "Lead"}</div>
                      <div className="text-[10px] font-black text-indigo-700 flex-shrink-0">{Math.round(l.priority_score)}</div>
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500 uppercase tracking-widest">{l.stage} · {l.source}</div>
                    <div className="mt-1 text-xs text-slate-600 truncate">{l.requested_service || l.last_message || "—"}</div>
                    {l.contact?.language && l.contact.language !== "en" && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
                        <Globe2 className="w-2.5 h-2.5" /> {l.contact.language}
                      </div>
                    )}
                    {l.claim && (
                      <div className="mt-1 text-[10px] text-amber-800 font-bold">Claimed by {l.claim.user_email?.split("@")[0]}</div>
                    )}
                  </button>
                ))}
              </div>
            </section>

            {/* Detail + call panel */}
            <section className="md:col-span-2 rounded-2xl bg-white border border-slate-200 p-5" data-testid="dialer-detail">
              {!selected ? (
                <div className="text-center text-sm text-slate-500 py-16">Pick a lead from the queue to claim it and start dialing.</div>
              ) : (
                <div>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Lead · {selected.stage}</div>
                      <div className="text-2xl font-black text-slate-900">{selected.contact?.name || selected.contact?.phone || "Lead"}</div>
                      <div className="mt-1 text-sm text-slate-600 space-x-3">
                        {selected.contact?.phone && <span className="font-mono">{selected.contact.phone}</span>}
                        {selected.contact?.email && <span>{selected.contact.email}</span>}
                      </div>
                    </div>
                    <button onClick={releaseLead} disabled={busy} data-testid="dialer-release"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 hover:border-rose-300 text-slate-700 px-3 py-2 text-sm font-semibold">
                      <PhoneOff className="w-4 h-4" /> Release
                    </button>
                  </div>

                  {/* Click-to-call panel */}
                  <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                    <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Click-to-call (MOCK)</div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Number (E.164)">
                        <input value={dial.number} onChange={(e) => setDial({ ...dial, number: e.target.value })}
                          data-testid="dialer-call-number" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
                      </Field>
                      <Field label="Language">
                        <select value={dial.language} onChange={(e) => setDial({ ...dial, language: e.target.value })}
                          data-testid="dialer-call-lang" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                          {LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Note (optional)">
                        <input value={dial.note} onChange={(e) => setDial({ ...dial, note: e.target.value })}
                          data-testid="dialer-call-note" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      </Field>
                    </div>
                    <div className="mt-3">
                      <button onClick={clickToCall} disabled={busy || !dial.number}
                        data-testid="dialer-call-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white px-5 py-2.5 font-semibold text-sm transition">
                        <PhoneCall className="w-4 h-4" /> Call now
                      </button>
                    </div>
                  </div>

                  {/* Disposition */}
                  <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Disposition</div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                      {DISPOSITIONS.map((d) => (
                        <button key={d.key} onClick={() => setDisp({ ...disp, disposition: d.key })}
                          data-testid={`dialer-disp-${d.key}`}
                          className={`text-left text-xs rounded-lg border p-2 font-semibold transition ${
                            disp.disposition === d.key
                              ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                              : "border-slate-200 hover:border-indigo-300 text-slate-700"
                          }`}>{d.label}</button>
                      ))}
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Duration (sec)">
                        <input value={disp.duration_seconds} onChange={(e) => setDisp({ ...disp, duration_seconds: e.target.value })} type="number"
                          data-testid="dialer-disp-duration" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      </Field>
                      <Field label="Revenue est. ($)">
                        <input value={disp.revenue_estimate} onChange={(e) => setDisp({ ...disp, revenue_estimate: e.target.value })} type="number"
                          data-testid="dialer-disp-rev" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      </Field>
                      <Field label="Notes">
                        <input value={disp.notes} onChange={(e) => setDisp({ ...disp, notes: e.target.value })}
                          data-testid="dialer-disp-notes" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="What happened?" />
                      </Field>
                    </div>
                    <div className="mt-3">
                      <button onClick={submitDisposition} disabled={busy}
                        data-testid="dialer-disp-submit"
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white px-5 py-2.5 font-semibold text-sm transition">
                        <CheckCircle2 className="w-4 h-4" /> Save disposition
                      </button>
                    </div>
                  </div>

                  {/* History */}
                  {history && (
                    <div className="mt-6">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">History</div>
                      <div className="mt-3 space-y-1 max-h-[240px] overflow-y-auto">
                        {history.dispositions?.map((d) => (
                          <div key={d.id} className="rounded border border-slate-100 bg-slate-50 p-2 text-xs">
                            <div className="flex justify-between"><strong>{d.disposition}</strong><span className="text-slate-500">{new Date(d.at).toLocaleString()}</span></div>
                            <div className="text-slate-600 mt-0.5">{d.notes || "—"}</div>
                          </div>
                        ))}
                        {history.calls?.map((c) => (
                          <div key={c.id} className="rounded border border-slate-100 bg-white p-2 text-xs">
                            <div className="flex justify-between"><strong>Call → {c.to_number}</strong><span className="text-slate-500">{new Date(c.started_at).toLocaleString()}</span></div>
                            <div className="text-slate-600 mt-0.5">{c.status} · {c.language || "en"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>);
}
function Tile({ label, value, tone, testId }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
                emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
                amber: "bg-amber-50 text-amber-800 border-amber-200",
                slate: "bg-slate-100 text-slate-700 border-slate-200" }[tone];
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-75">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}
